/**
 * Guía: Compañías.
 *
 * Contenido derivado del capítulo **12** del manual: §0 y §2 (por qué hay
 * tres pantallas sobre la misma entidad), §3.1 (declarar un aliado no es
 * concederle comisión), §4 (el ciclo de vida y quién mueve cada transición),
 * §5 (la aprobación y sus cinco validaciones) y §6 (los asesores asignados).
 *
 * La aprobación va sin objetivo: vive en un diálogo que se abre desde el
 * detalle de una compañía, y el motor no pulsa por nadie. Es además el paso
 * que más importa de esta guía — aprobar es el acto que hace nacer el
 * dinero— así que se explica antes de que alguien llegue a ese botón.
 */

import type { TourFlow } from '../types';

const COMPANIAS_PATH = '/companies';

export const c12Companias: TourFlow = {
  id: 'admin.companies',
  chapter: '12',
  title: 'Compañías',
  summary:
    'El ciclo de vida de un comercio y por qué aprobarlo es el acto que hace nacer el dinero.',
  href: COMPANIAS_PATH,
  requiresCountry: true,
  estimatedMin: 5,
  steps: [
    {
      id: 'intro',
      title: 'La unidad de negocio de la plataforma',
      body:
        'Una compañía es el comercio que vende en 121Pass: agrupa sus sucursales, su ' +
        'catálogo, su equipo, sus cuentas bancarias y sus cobros. Esta pantalla gobierna su ' +
        'ciclo de vida, desde que alguien se registra hasta que vende.',
      route: COMPANIAS_PATH,
    },
    {
      id: 'three-screens',
      title: 'Hay tres pantallas sobre lo mismo',
      body:
        'Ésta es el listado completo del país y es la única desde la que se aprueba, se ' +
        'rechaza y se suspende. «Mis Compañías» muestra las que un asesor tiene asignadas, ' +
        'y «Compañías (Soporte)» es la vista de soporte. Misma entidad, permisos distintos: ' +
        'si buscas una compañía y no la ves, comprueba en cuál de las tres estás.',
    },
    {
      id: 'stats',
      title: 'El pulso del país',
      body:
        'Cuántas compañías hay y en qué estado. Es lo que dice si hay trabajo pendiente: ' +
        'una cifra alta de pendientes son comercios registrados esperando a que alguien ' +
        'los apruebe.',
      target: 'admin.companies.stats',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'lifecycle',
      title: 'Cuatro estados, y quién los mueve',
      body:
        'Una compañía nace Pendiente al registrarse. De ahí pasa a Activa —aprobada— o a ' +
        'Rechazada, con un motivo que el comercio va a leer. Una activa se puede suspender, ' +
        'también con motivo, y una suspendida o rechazada se reactiva. Las cuatro ' +
        'transiciones son del administrador general y todas quedan auditadas.',
      target: 'admin.companies.statusTabs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'filters',
      title: 'Buscar y acotar',
      body:
        'Busca por nombre o identificación fiscal, y acota por verificación. El contador ' +
        'de resultados es del filtro completo, no de la página que estás viendo.',
      target: 'admin.companies.filters',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'map',
      title: 'La misma lista, sobre un mapa',
      body:
        'La vista de mapa sitúa las compañías de la página cargada. Sirve para ver ' +
        'cobertura por zona; el paginador sigue mandando, así que el mapa muestra la ' +
        'página que tengas, no todo el país de una vez.',
      target: 'admin.companies.viewToggle',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'approve-is-money',
      title: 'Aprobar es donde nace el dinero',
      body:
        'No es un visto bueno administrativo. Al aprobar se sella el canal por el que llegó ' +
        'el comercio y, si vino de un aliado, se crea la fila que le concede comisión sobre ' +
        'sus ventas durante una ventana de meses. Por eso el diálogo pide gestor de cuenta ' +
        'y canal antes de dejarte continuar.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'declared-vs-granted',
      title: 'Que declare un aliado no le da comisión',
      body:
        'Cualquiera puede registrarse con el enlace de invitación de un aliado. Ese dato ' +
        'sólo declara: rellena la decisión, no la toma. La comisión nace cuando tú eliges ' +
        'el canal «asesor externo» al aprobar. Si eso bastara con registrarse, cobrar sería ' +
        'trivial para cualquiera.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'advisor-must-be-active',
      title: 'Un aliado suspendido no empieza a cobrar',
      body:
        'Al indicar el aliado, el servidor comprueba que su perfil de asesor esté activo, ' +
        'no sólo que la persona exista. Es la validación que protege el dinero: sin ella, ' +
        'un asesor dado de baja seguiría acumulando comisión con cada comercio nuevo.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'reject-reason',
      title: 'El motivo del rechazo lo lee el comercio',
      body:
        'Rechazar y suspender exigen motivo, y no es una nota interna: la empresa lo ve. ' +
        'Escríbelo pensando en quién lo va a leer, porque es lo único que tiene para saber ' +
        'qué corregir.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'detail',
      title: 'El detalle, y quién puede tocarlo',
      body:
        'Al abrir una compañía verás sus datos, sus sucursales y sus asesores asignados. La ' +
        'página se pone en sólo lectura si tu rol no es el de administrador general: ' +
        'consultar sí, cambiar no.',
      target: 'admin.companies.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'unassign',
      title: 'Quitar un asesor no borra su historial',
      body:
        'Desasignar marca la fecha de baja en vez de borrar la fila. Tiene que ser así: las ' +
        'comisiones que ya generó cuelgan de ella, y borrarla dejaría pagos sin explicación.',
      roles: ['SUPER_ADMIN', 'SUPPORT_LEAD'],
    },
  ],
};
